import { forwardRef, useImperativeHandle, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { type Candidate } from '../lib/api'
import SwipeCard from './SwipeCard'

interface SwipeDeckProps {
  candidates: Candidate[]
  index: number
  onSwipe: (action: 'like' | 'skip' | 'crush') => Promise<void>
  disabled: boolean
}

export interface SwipeDeckHandle {
  swipe: (action: 'like' | 'skip' | 'crush') => void
}

const SwipeDeck = forwardRef<SwipeDeckHandle, SwipeDeckProps>(function SwipeDeck(
  { candidates, index, onSwipe, disabled },
  ref,
) {
  const [programmaticAction, setProgrammaticAction] = useState<'like' | 'skip' | 'crush' | null>(null)

  useImperativeHandle(ref, () => ({
    swipe(action) {
      if (disabled || programmaticAction) return
      setProgrammaticAction(action)
    },
  }))

  // Visible cards: top (index), then up to 2 behind
  const visibleSlots = [0, 1, 2].map((offset) => candidates[index + offset]).filter(Boolean)

  return (
    // Outer wrapper gives the deck a fixed height relative to the flex layout
    <div className="relative flex-1">
      <AnimatePresence>
        {visibleSlots.map((candidate, slotIndex) => {
          const isTop = slotIndex === 0
          const scale = 1 - slotIndex * 0.05
          const translateY = slotIndex * 8

          if (isTop) {
            return (
              <SwipeCard
                key={candidate.user_id}
                candidate={candidate}
                nextCandidate={candidates[index + 1]}
                onSwipe={onSwipe}
                disabled={disabled}
                programmaticAction={programmaticAction}
                onProgrammaticDone={() => setProgrammaticAction(null)}
              />
            )
          }

          return (
            <motion.div
              key={candidate.user_id}
              className="absolute inset-0 overflow-hidden rounded-2xl bg-tg-secondary-bg shadow-md"
              initial={{ scale, y: translateY }}
              animate={{ scale, y: translateY }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              style={{ zIndex: -slotIndex }}
            />
          )
        })}
      </AnimatePresence>
    </div>
  )
})

export default SwipeDeck
